import {createPortal} from "react-dom";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Pencil, Archive, Trash2, Layers, ChevronRight,
    Search, X, Save, RotateCcw, AlertTriangle, Check, Wand2, ShieldCheck,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";

/**
 * Sprint 33 — Portal-Owned Form Categories.
 *
 * Two views in one page (per user amendment: service mapping stays INSIDE
 * the category view, not a separate page):
 *  - List view: every category as a card (name, template count, mapping count,
 *    edit / archive / delete). Reordering via up/down arrows.
 *  - Detail view (when a category is selected): rename + archive/reactivate,
 *    plus the mapping picker — search Amelia services, add/remove chips
 *    directly on the category. Unmapped services alerted at the top.
 */
export function StaffFormCategories() {
    const nav = useNavigate();
    const [cats, setCats] = useState(null);
    const [services, setServices] = useState([]);
    const [allMappings, setAllMappings] = useState([]);
    const [unmapped, setUnmapped] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState("");
    const [showAutoMap, setShowAutoMap] = useState(false);
    const [view,setView]=useState("active"),[deleting,setDeleting]=useState(null),[target,setTarget]=useState("");

    const load = async () => {
        setError(null);
        try {
            const [c, s, m, u] = await Promise.all([
                staffApi.listFormCategories(true),
                staffApi.services(),
                staffApi.listAllServiceMappings(),
                staffApi.listUnmappedServices(),
            ]);
            setCats(c.categories || []);
            setServices(s.services || []);
            setAllMappings(m.mappings || []);
            setUnmapped(u.unmapped || []);
        } catch (e) { setError(e); }
    };
    useEffect(() => { load(); }, []);

    const doCreate = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            await staffApi.createFormCategory({ name: newName.trim() });
            toast.success("Category created");
            setNewName(""); setShowCreate(false);
            load();
        } catch (e) { toast.error(e?.message || "Create failed"); }
        finally { setBusy(false); }
    };

    const doArchive = async (cat) => {
        if (!window.confirm(`Archive "${cat.name}"? It will be hidden but existing forms and history stay intact.`)) return;
        try { await staffApi.patchFormCategory(cat.id, { status: "archived" }); toast.success("Archived"); load(); }
        catch (e) { toast.error(e?.message || "Archive failed"); }
    };

    const doRestore = async (cat) => {
        try { await staffApi.patchFormCategory(cat.id, { status: "active" }); toast.success("Restored"); load(); }
        catch (e) { toast.error(e?.message || "Restore failed"); }
    };

    const doDelete = (cat) => {setDeleting(cat);setTarget('');};
    const confirmDelete=async()=>{
        setBusy(true);
        try{await staffApi.deleteFormCategory(deleting.id,target);setDeleting(null);toast.success('Category deleted');await load();}
        catch(e){toast.error(e.message || 'Delete failed');}finally{setBusy(false);}
    };

    const doMove = async (cat, direction) => {
        const active = (cats || []).filter((c) => c.status !== "archived");
        const idx = active.findIndex((c) => c.id === cat.id);
        const swap = direction === "up" ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= active.length) return;
        const newOrder = active.slice();
        [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
        try {
            await staffApi.reorderFormCategories(newOrder.map((c) => c.id));
            load();
        } catch (e) { toast.error(e?.message || "Reorder failed"); }
    };

    const selected = useMemo(
        () => (cats || []).find((c) => c.id === selectedId) || null,
        [cats, selectedId],
    );

    if (error) return <ErrorState error={error} onRetry={load} />;
    if (!cats) return <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;

    // Detail view
    if (selected) return (
        <CategoryDetail
            category={selected}
            services={services}
            allMappings={allMappings}
            onBack={() => { setSelectedId(null); load(); }}
            onChanged={load}
        />
    );

    // List view
    return (
        <div className="space-y-5" data-testid="form-categories-page">
            <header className="flex flex-wrap items-baseline gap-3 justify-between">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/50">
                        <Link to="/staff/forms" className="hover:text-gold inline-flex items-center gap-1">
                            <ArrowLeft className="h-3 w-3" /> Back to Forms
                        </Link>
                    </div>
                    <h1 className="font-serif font-light text-3xl md:text-4xl text-white flex items-center gap-3 mt-1">
                        <Layers className="h-6 w-6 text-gold" /> Manage categories
                    </h1>
                    <p className="text-sm text-white/50 mt-1">
                        Group treatments into categories. Each category holds the forms and services it applies to.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowAutoMap(true)}
                        data-testid="fc-auto-map-btn"
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold/10"
                    >
                        <Wand2 className="h-4 w-4" /> Auto-map suggestions
                    </button>
                    <button
                        onClick={() => setShowCreate(true)}
                        data-testid="fc-create-btn"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm"
                    >
                        <Plus className="h-4 w-4" /> New category
                    </button>
                </div>
            </header>

            {unmapped.length > 0 && (
                <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="fc-unmapped-banner">
                    <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-100 flex-1">
                        <div className="font-semibold">
                            {unmapped.length} service{unmapped.length === 1 ? "" : "s"} not yet mapped to a category
                        </div>
                        <p className="text-amber-100/80 mt-1">
                            These bookings will fall back to legacy name-matching. Open a category and add the missing services to lock in the mapping.
                        </p>
                        <ul className="mt-2 text-xs space-y-0.5 max-h-24 overflow-auto">
                            {unmapped.slice(0, 8).map((u) => (
                                <li key={u.amelia_service_id} data-testid={`fc-unmapped-${u.amelia_service_id}`}>
                                    • {u.amelia_service_name || `Service #${u.amelia_service_id}`}
                                </li>
                            ))}
                            {unmapped.length > 8 && <li>• …and {unmapped.length - 8} more</li>}
                        </ul>
                    </div>
                </div>
            )}

            <div className="flex gap-3"><button onClick={()=>setView("active")} aria-pressed={view==="active"}>Active categories</button><button onClick={()=>setView("archived")} aria-pressed={view==="archived"}>Archived categories ({cats.filter(c=>c.status==="archived").length})</button></div>
            <div className="grid gap-3 md:grid-cols-2" data-testid="fc-grid">
                {cats.filter(c=>view==="archived"?c.status==="archived":c.status!=="archived").map((c) => (
                    <CategoryCard
                        key={c.id}
                        category={c}
                        onOpen={() => setSelectedId(c.id)}
                        onMoveUp={() => doMove(c, "up")}
                        onMoveDown={() => doMove(c, "down")}
                        onArchive={() => doArchive(c)}
                        onRestore={() => doRestore(c)}
                        onDelete={() => doDelete(c)}
                    />
                ))}
            </div>

            {deleting && createPortal(<div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Delete form category"><div className="surface-highlight rounded-2xl p-6 w-full max-w-lg space-y-4">
                <h2 className="text-white text-xl">Delete {deleting.name}?</h2><p className="text-white/80">Completed forms and signatures will be kept. This category will be removed from the list.</p>
                {(deleting.template_count>0 || deleting.mapping_count>0) && <label className="block text-white">Move its forms and assigned services to<select className="block w-full mt-2 bg-white text-navy rounded-lg p-3" value={target} onChange={e=>setTarget(e.target.value)}><option value="">Choose a category</option>{cats.filter(c=>c.id!==deleting.id && c.status==='active').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select><span className="text-sm">Create another category first if there is no suitable destination.</span></label>}
                <div className="flex gap-3"><button disabled={busy} onClick={()=>setDeleting(null)} className="border rounded-full px-5 py-3 text-white">Keep category</button><button disabled={busy || ((deleting.template_count>0 || deleting.mapping_count>0) && !target)} onClick={confirmDelete} className="bg-gold text-navy rounded-full px-5 py-3 disabled:opacity-40">{busy?'Deleting…':'Confirm delete'}</button></div>
            </div></div>,document.body)}
            {showCreate && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowCreate(false)}>
                    <div onClick={(e) => e.stopPropagation()} data-testid="fc-create-modal"
                         className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5 space-y-3">
                        <h3 className="font-serif text-white text-xl">New category</h3>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 block">Name</label>
                        <input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                               placeholder="e.g. LED Therapy" data-testid="fc-create-name"
                               className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setShowCreate(false)}
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                            <button onClick={doCreate} disabled={busy || !newName.trim()}
                                    data-testid="fc-create-submit"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAutoMap && (
                <AutoMapReviewModal
                    categories={cats}
                    onClose={() => setShowAutoMap(false)}
                    onApplied={() => { setShowAutoMap(false); load(); }}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Auto-map suggestion review — suggestions ONLY. Nothing is saved until the
// staff clicks "Apply N mappings" at the end. Every row can be accept/reject
// individually, low-confidence rows are clearly flagged, and the target
// category can be overridden per row.
// ---------------------------------------------------------------------------
function AutoMapReviewModal({ categories, onClose, onApplied }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [counts, setCounts] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [applying, setApplying] = useState(false);
    const [filter, setFilter] = useState("suggestable");

    useEffect(() => {
        setLoading(true);
        staffApi.suggestServiceMappings()
            .then((r) => {
                setProposals(r.proposals || []);
                setCounts(r.counts || null);
                const init = {};
                (r.proposals || []).forEach((p) => {
                    init[p.amelia_service_id] = {
                        accepted: p.confidence === "high",
                        category_id: p.suggested_category_id || null,
                    };
                });
                setDecisions(init);
            })
            .catch(setError)
            .finally(() => setLoading(false));
    }, []);

    const activeCategories = useMemo(
        () => (categories || []).filter((c) => c.status !== "archived"),
        [categories],
    );

    const visible = useMemo(() => {
        if (filter === "all") return proposals;
        return proposals.filter((p) => p.confidence !== "already_mapped");
    }, [proposals, filter]);

    const acceptedItems = useMemo(() => {
        return proposals
            .filter((p) => {
                const d = decisions[p.amelia_service_id];
                return d && d.accepted && d.category_id && p.confidence !== "already_mapped";
            })
            .map((p) => ({
                amelia_service_id: p.amelia_service_id,
                amelia_service_name: p.amelia_service_name,
                category_id: decisions[p.amelia_service_id].category_id,
                _proposal: p,
            }));
    }, [proposals, decisions]);

    const setDecision = (sid, patch) => setDecisions((d) => ({
        ...d,
        [sid]: { ...(d[sid] || { accepted: false, category_id: null }), ...patch },
    }));

    const acceptAllHighConfidence = () => {
        setDecisions((prev) => {
            const next = { ...prev };
            proposals.forEach((p) => {
                if (p.confidence === "high") {
                    next[p.amelia_service_id] = {
                        accepted: true,
                        category_id: p.suggested_category_id,
                    };
                }
            });
            return next;
        });
    };

    const rejectAll = () => {
        setDecisions((prev) => {
            const next = { ...prev };
            proposals.forEach((p) => {
                next[p.amelia_service_id] = {
                    ...(next[p.amelia_service_id] || {}),
                    accepted: false,
                };
            });
            return next;
        });
    };

    const doApply = async () => {
        if (acceptedItems.length === 0) return;
        setApplying(true);
        try {
            const payload = acceptedItems.map(({ _proposal, ...rest }) => rest);
            const r = await staffApi.bulkApplyServiceMappings(payload);
            const total = (r.applied || 0) + (r.reassigned || 0);
            if (r.errors && r.errors.length > 0) {
                toast.error(`${r.errors.length} row(s) failed. ${total} saved.`);
            } else {
                toast.success(`${total} mapping${total === 1 ? "" : "s"} saved.`);
            }
            onApplied();
        } catch (e) {
            toast.error(e?.message || "Bulk apply failed");
        } finally {
            setApplying(false);
            setConfirmOpen(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch sm:items-center justify-center"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                data-testid="fc-auto-map-modal"
                className="w-full sm:max-w-4xl m-0 sm:m-4 rounded-none sm:rounded-2xl glass-strong border hairline flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden"
            >
                <header className="p-4 sm:p-5 border-b border-white/10 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="font-serif text-white text-xl sm:text-2xl flex items-center gap-2">
                            <Wand2 className="h-5 w-5 text-gold" /> Auto-map suggestions
                        </h2>
                        <p className="text-xs text-white/60 mt-1 max-w-2xl">
                            These are proposals only. Nothing is saved until you review each row and press <span className="text-gold">Apply</span>. Low-confidence rows are flagged so you can decide manually.
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="fc-auto-map-close"
                            className="text-white/60 hover:text-white p-1">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {loading && (
                    <div className="p-8 text-center text-white/60 text-sm">
                        Reading services…
                    </div>
                )}
                {error && (
                    <div className="p-6">
                        <ErrorState error={error} onRetry={onClose} />
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div className="px-4 sm:px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2 justify-between">
                            <div className="flex items-center gap-3 text-xs text-white/70 flex-wrap" data-testid="fc-auto-map-counts">
                                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" />{counts?.high || 0} high confidence</span>
                                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />{counts?.low || 0} low confidence</span>
                                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white/40" />{counts?.already_mapped || 0} already mapped</span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <select value={filter} onChange={(e) => setFilter(e.target.value)}
                                        data-testid="fc-auto-map-filter"
                                        className="bg-navy border hairline rounded-md text-xs text-white px-2 h-8">
                                    <option value="suggestable">Suggestions only</option>
                                    <option value="all">Include already-mapped</option>
                                </select>
                                <button onClick={acceptAllHighConfidence} data-testid="fc-auto-map-accept-high"
                                        className="text-xs text-emerald-200 h-8 px-3 rounded-full border border-emerald-300/40 hover:bg-emerald-500/10">
                                    Accept all high-confidence
                                </button>
                                <button onClick={rejectAll} data-testid="fc-auto-map-reject-all"
                                        className="text-xs text-white/70 h-8 px-3 rounded-full border border-white/20 hover:bg-white/10">
                                    Reject all
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto divide-y divide-white/5" data-testid="fc-auto-map-list">
                            {visible.length === 0 && (
                                <div className="p-8 text-center text-white/60 text-sm">
                                    No proposals to review with current filter.
                                </div>
                            )}
                            {visible.map((p) => {
                                const d = decisions[p.amelia_service_id] || {};
                                const isAlready = p.confidence === "already_mapped";
                                const isHigh = p.confidence === "high";
                                const isLow = p.confidence === "low";
                                return (
                                    <div key={p.amelia_service_id}
                                         data-testid={`fc-proposal-${p.amelia_service_id}`}
                                         className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <input
                                                type="checkbox"
                                                checked={!!d.accepted}
                                                disabled={isAlready}
                                                onChange={(e) => setDecision(p.amelia_service_id, { accepted: e.target.checked })}
                                                data-testid={`fc-proposal-accept-${p.amelia_service_id}`}
                                                className="h-4 w-4 accent-gold shrink-0"
                                            />
                                            <div className="min-w-0">
                                                <div className="text-sm text-white truncate">{p.amelia_service_name || `Service #${p.amelia_service_id}`}</div>
                                                <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mt-0.5">
                                                    Service #{p.amelia_service_id}
                                                    {p.amelia_category_name && <> · {p.amelia_category_name}</>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            {isHigh && (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/40 text-emerald-200">
                                                    <ShieldCheck className="h-3 w-3" /> High
                                                </span>
                                            )}
                                            {isLow && (
                                                <span
                                                    data-testid={`fc-proposal-flag-${p.amelia_service_id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-amber-500/10 border border-amber-400/40 text-amber-200">
                                                    <AlertTriangle className="h-3 w-3" /> Needs review
                                                </span>
                                            )}
                                            {isAlready && (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-white/5 border border-white/15 text-white/60">
                                                    Mapped
                                                </span>
                                            )}
                                            <select
                                                value={d.category_id || ""}
                                                disabled={isAlready}
                                                onChange={(e) => setDecision(p.amelia_service_id, {
                                                    category_id: e.target.value || null,
                                                    accepted: !!e.target.value,
                                                })}
                                                data-testid={`fc-proposal-category-${p.amelia_service_id}`}
                                                className="bg-navy border hairline rounded-md text-xs text-white px-2 h-8 min-w-[10rem]"
                                            >
                                                <option value="">— Choose category —</option>
                                                {activeCategories.map((c) => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <footer className="p-4 sm:p-5 border-t border-white/10 flex flex-wrap items-center gap-3 justify-between">
                            <div className="text-sm text-white/70" data-testid="fc-auto-map-summary">
                                <span className="text-gold font-semibold">{acceptedItems.length}</span>{" "}
                                mapping{acceptedItems.length === 1 ? "" : "s"} ready to save
                            </div>
                            <div className="flex gap-2">
                                <button onClick={onClose}
                                        className="h-10 px-4 rounded-full border hairline text-white/80 text-sm">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={acceptedItems.length === 0 || applying}
                                    data-testid="fc-auto-map-apply"
                                    className="h-10 px-5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-2"
                                >
                                    <Check className="h-4 w-4" /> Apply {acceptedItems.length || ""}
                                </button>
                            </div>
                        </footer>
                    </>
                )}
            </div>

            {confirmOpen && (
                <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center"
                     onClick={() => !applying && setConfirmOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()}
                         data-testid="fc-auto-map-confirm"
                         className="w-full max-w-md m-4 rounded-2xl glass-strong border hairline p-5 space-y-3">
                        <h3 className="font-serif text-white text-xl flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-gold" /> Confirm save
                        </h3>
                        <p className="text-sm text-white/70">
                            You're about to save <span className="text-gold font-semibold">{acceptedItems.length}</span> service→category mapping{acceptedItems.length === 1 ? "" : "s"}. These will override the legacy name matcher immediately.
                        </p>
                        <ul className="text-xs text-white/60 max-h-40 overflow-auto space-y-1 rounded-lg bg-white/[0.04] p-3">
                            {acceptedItems.slice(0, 8).map((it) => {
                                const cat = activeCategories.find((c) => c.id === it.category_id);
                                return (
                                    <li key={it.amelia_service_id}>
                                        <span className="text-white/80">{it.amelia_service_name}</span>
                                        <span className="text-white/40"> → </span>
                                        <span className="text-gold">{cat?.name || "?"}</span>
                                    </li>
                                );
                            })}
                            {acceptedItems.length > 8 && (
                                <li className="text-white/50">…and {acceptedItems.length - 8} more</li>
                            )}
                        </ul>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setConfirmOpen(false)} disabled={applying}
                                    data-testid="fc-auto-map-confirm-cancel"
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">
                                Back
                            </button>
                            <button onClick={doApply} disabled={applying}
                                    data-testid="fc-auto-map-confirm-save"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1">
                                {applying ? "Saving…" : <>Save {acceptedItems.length}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
// eslint-disable-next-line

function CategoryCard({ category: c, onOpen, onMoveUp, onMoveDown, onArchive, onRestore, onDelete }) {
    const archived = c.status === "archived";
    return (
        <div
            className={`rounded-2xl bg-[#0E1746] surface-highlight shadow-lg overflow-hidden ${archived ? "opacity-60" : ""}`}
            data-testid={`fc-card-${c.slug}`}
        >
            <button
                onClick={onOpen}
                data-testid={`fc-open-${c.slug}`}
                className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/[0.05] transition-colors"
            >
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold tracking-tight text-xl md:text-[22px] truncate">{c.name}</span>
                        {c.is_system && (
                            <span className="text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-gold/40 text-gold/90">
                                System
                            </span>
                        )}
                        {archived && (
                            <span className="text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-red-400/40 text-red-200 bg-red-500/10">
                                Archived
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1 flex gap-3">
                        <span>{c.template_count} form{c.template_count === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{c.mapping_count} service{c.mapping_count === 1 ? "" : "s"} mapped</span>
                    </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gold shrink-0" />
            </button>
            <div className="px-4 pb-3 pt-1 flex items-center gap-1.5 flex-wrap border-t border-white/10">
                {!archived ? (
                    <>
                        <button onClick={onMoveUp} title="Move up" data-testid={`fc-up-${c.slug}`}
                                className="text-xs text-white/60 h-7 px-2 rounded-full border border-white/15 hover:text-gold hover:border-gold/50">▲</button>
                        <button onClick={onMoveDown} title="Move down" data-testid={`fc-down-${c.slug}`}
                                className="text-xs text-white/60 h-7 px-2 rounded-full border border-white/15 hover:text-gold hover:border-gold/50">▼</button>
                        <button onClick={onArchive} data-testid={`fc-archive-${c.slug}`}
                                className="ml-auto inline-flex items-center gap-1 text-xs text-white/60 h-7 px-2 rounded-full border border-white/15 hover:text-red-300 hover:border-red-300/40">
                            <Archive className="h-3 w-3" /> Archive
                        </button>
                    </>
                ) : (
                    <>
                        <button onClick={onRestore} data-testid={`fc-restore-${c.slug}`}
                                className="inline-flex items-center gap-1 text-xs text-white/80 h-7 px-2 rounded-full border border-white/15 hover:text-gold hover:border-gold/50">
                            <RotateCcw className="h-3 w-3" /> Restore
                        </button>

                    </>
                )}<button onClick={onDelete} data-testid={`fc-delete-${c.slug}`} className="ml-auto inline-flex items-center gap-1 text-xs text-red-400 h-9 px-3 rounded-full border border-red-400/50"><Trash2 className="h-3 w-3"/> Delete</button>
            </div>
        </div>
    );
}


function CategoryDetail({ category, services, allMappings, onBack, onChanged }) {
    const [name, setName] = useState(category.name);
    const [mappings, setMappings] = useState(null);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);

    const loadMappings = async () => {
        try {
            const r = await staffApi.listCategoryServiceMappings(category.id);
            setMappings(r.mappings || []);
        } catch (e) { toast.error(e?.message || "Could not load mappings"); }
    };
    useEffect(() => { loadMappings(); /* eslint-disable-next-line */ }, [category.id]);

    // service_id → { category_id, category_name } for global mapping lookup
    const mappedElsewhere = useMemo(() => {
        const map = new Map();
        (allMappings || []).forEach((m) => {
            if (m.category_id !== category.id) {
                map.set(m.amelia_service_id, m);
            }
        });
        return map;
    }, [allMappings, category.id]);

    const currentIds = useMemo(
        () => new Set((mappings || []).map((m) => m.amelia_service_id)),
        [mappings],
    );

    const available = useMemo(() => {
        const term = q.trim().toLowerCase();
        return (services || [])
            .filter((s) => !currentIds.has(s.id))
            .filter((s) => !term || (s.name || "").toLowerCase().includes(term))
            .slice(0, 40);
    }, [services, currentIds, q]);

    const doRename = async () => {
        const trimmed = name.trim();
        if (!trimmed || trimmed === category.name) return;
        setBusy(true);
        try {
            await staffApi.patchFormCategory(category.id, { name: trimmed });
            toast.success("Renamed");
            onChanged();
        } catch (e) { toast.error(e?.message || "Rename failed"); }
        finally { setBusy(false); }
    };

    const doAdd = async (service) => {
        setBusy(true);
        try {
            const r = await staffApi.addCategoryServiceMapping(category.id, {
                amelia_service_id: service.id,
                amelia_service_name: service.name,
            });
            toast.success(r.reassigned ? "Reassigned to this category" : "Added");
            await loadMappings();
            onChanged();
        } catch (e) { toast.error(e?.message || "Add failed"); }
        finally { setBusy(false); }
    };

    const doRemove = async (m) => {
        setBusy(true);
        try {
            await staffApi.removeCategoryServiceMapping(category.id, m.id);
            toast.success("Removed");
            await loadMappings();
            onChanged();
        } catch (e) { toast.error(e?.message || "Remove failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-5" data-testid={`fc-detail-${category.slug}`}>
            <header className="flex flex-wrap items-baseline gap-3 justify-between">
                <div>
                    <button onClick={onBack} data-testid="fc-detail-back"
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-gold">
                        <ArrowLeft className="h-3 w-3" /> Back to categories
                    </button>
                    <h1 className="font-serif font-light text-3xl md:text-4xl text-white flex items-center gap-3 mt-2">
                        <Layers className="h-6 w-6 text-gold" /> {category.name}
                    </h1>
                    <p className="text-xs text-white/45 mt-1">
                        slug: <span className="font-mono">{category.slug}</span>
                        {category.is_system && <span className="ml-3 text-gold/80">SYSTEM</span>}
                        {category.status === "archived" && <span className="ml-3 text-red-300">ARCHIVED</span>}
                    </p>
                </div>
            </header>

            {/* Rename */}
            <section className="glass rounded-2xl p-4 border hairline space-y-3" data-testid="fc-detail-rename">
                <h2 className="text-[10px] uppercase tracking-[0.22em] text-gold/80 flex items-center gap-2">
                    <Pencil className="h-3.5 w-3.5" /> Category name
                </h2>
                <div className="flex gap-2">
                    <input value={name} onChange={(e) => setName(e.target.value)}
                           data-testid="fc-detail-name-input"
                           className="flex-1 h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                    <button onClick={doRename} disabled={busy || !name.trim() || name.trim() === category.name}
                            data-testid="fc-detail-save"
                            className="h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-1 disabled:opacity-40">
                        <Save className="h-4 w-4" /> Save
                    </button>
                </div>
                <p className="text-xs text-white/40">
                    The slug is fixed for system categories to keep automations stable.
                </p>
            </section>

            {/* Service Mappings — INLINE per user amendment */}
            <section className="glass rounded-2xl p-4 border hairline space-y-4" data-testid="fc-detail-mappings">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h2 className="text-[10px] uppercase tracking-[0.22em] text-gold/80">
                        Services mapped to this category
                    </h2>
                    <span className="text-xs text-white/50">{(mappings || []).length} mapped</span>
                </div>

                {/* Current chips */}
                {mappings === null ? (
                    <Skeleton className="h-14" />
                ) : mappings.length === 0 ? (
                    <p className="text-sm text-white/50" data-testid="fc-detail-mappings-empty">
                        No services mapped yet. Add one from the list below so automations and medical form dispatch use this category.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="fc-detail-mapping-chips">
                        {mappings.map((m) => (
                            <span key={m.id}
                                  data-testid={`fc-mapping-${m.amelia_service_id}`}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs">
                                <Check className="h-3 w-3" />
                                {m.amelia_service_name || `Service #${m.amelia_service_id}`}
                                <button onClick={() => doRemove(m)} title="Remove"
                                        data-testid={`fc-mapping-remove-${m.amelia_service_id}`}
                                        className="hover:text-red-300">
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Add services picker */}
                {category.status !== "archived" && (
                    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <Search className="h-4 w-4 text-white/40 shrink-0" />
                            <input value={q} onChange={(e) => setQ(e.target.value)}
                                   placeholder="Search services to add…"
                                   data-testid="fc-detail-search"
                                   className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none" />
                        </div>
                        <div className="max-h-64 overflow-auto divide-y divide-white/5">
                            {available.length === 0 && (
                                <p className="text-xs text-white/40 py-3 text-center">
                                    {q ? "No matching services." : "All services are already mapped somewhere."}
                                </p>
                            )}
                            {available.map((s) => {
                                const elsewhere = mappedElsewhere.get(s.id);
                                return (
                                    <div key={s.id}
                                         data-testid={`fc-service-${s.id}`}
                                         className="flex items-center justify-between gap-2 py-2">
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{s.name}</div>
                                            {elsewhere && (
                                                <div className="text-[10px] uppercase tracking-[0.14em] text-amber-300/80">
                                                    Currently in another category — adding will reassign it here
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => doAdd(s)}
                                            disabled={busy}
                                            data-testid={`fc-add-service-${s.id}`}
                                            className="inline-flex items-center gap-1 text-xs text-navy bg-gold h-8 px-3 rounded-full font-semibold disabled:opacity-40 shrink-0"
                                        >
                                            <Plus className="h-3 w-3" /> Add
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

export default StaffFormCategories;
